class ProductInfoTabs extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  #buttons = [];
  /** @type {HTMLElement[]} */
  #panels = [];
  /** @type {HTMLDetailsElement[]} */
  #mobileItems = [];
  /** @type {HTMLElement | null} */
  #tabList = null;
  /** @type {HTMLElement | null} */
  #inner = null;
  #activeId = '';
  #initialized = false;
  #mobileBreakpoint = window.matchMedia('(min-width: 990px)');
  /** @type {IntersectionObserver | null} */
  #videoObserver = null;
  #onResize = () => {
    if (!this.#activeId) return;
    this.#updateActiveOffset(this.#activeId);
  };
  #onHashChange = () => {
    this.#activateFromHash(true);
  };
  /** @param {Event} event */
  #onMobileToggle = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLDetailsElement)) return;
    if (!target.open) return;
    const tabId = target.dataset.tabId;
    if (!tabId) return;
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
    this.#replaceHash(tabId);
  };

  connectedCallback() {
    if (this.#initialized) return;

    this.#buttons = Array.from(this.querySelectorAll('[role="tab"]'));
    this.#panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.#mobileItems = Array.from(this.querySelectorAll('.product-info-tabs__mobile-item[data-tab-id]'));
    this.#tabList = this.querySelector('.product-info-tabs__tab-list');
    this.#inner = this.querySelector('.product-info-tabs__inner');

    if ((!this.#buttons.length || !this.#panels.length) && !this.#mobileItems.length) return;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);
    window.addEventListener('resize', this.#onResize);
    window.addEventListener('hashchange', this.#onHashChange);
    for (const item of this.#mobileItems) {
      item.addEventListener('toggle', this.#onMobileToggle);
    }

    const initialTab = this.dataset.initialTab;
    const initialButton = this.#buttons.find((button) => button.dataset.tabId === initialTab) || this.#buttons[0];
    if (initialButton) {
      this.#activateTab(initialButton.dataset.tabId);
    }

    this.#activateFromHash(false);

    // Setup video intersection observer for 80% visibility
    if ('IntersectionObserver' in window) {
      this.#videoObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) continue;
          if (entry.isIntersecting) {
            if (video.hasAttribute('autoplay') || video.autoplay) {
              video.play().catch(() => {});
            }
          } else {
            video.pause();
          }
        }
      }, {
        threshold: 0.8
      });

      const videos = this.querySelectorAll('.product-info-tabs__media video');
      for (const video of videos) {
        this.#videoObserver.observe(video);
      }
    }

    this.#initialized = true;
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeyDown);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('hashchange', this.#onHashChange);
    for (const item of this.#mobileItems) {
      item.removeEventListener('toggle', this.#onMobileToggle);
    }
    if (this.#videoObserver) {
      this.#videoObserver.disconnect();
      this.#videoObserver = null;
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[role="tab"]');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    this.#activateTab(button.dataset.tabId, true, true);
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLButtonElement)) return;
    if (target.getAttribute('role') !== 'tab') return;

    const currentIndex = this.#buttons.indexOf(target);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;
    const lastIndex = this.#buttons.length - 1;

    if (event.key === 'ArrowRight') {
      nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = lastIndex;
    } else {
      return;
    }

    event.preventDefault();
    const nextButton = this.#buttons[nextIndex];
    if (!nextButton) return;
    this.#activateTab(nextButton.dataset.tabId, true, true);
  };

  /**
   * @param {string | undefined} tabId
   * @param {boolean} [shouldFocus]
   * @param {boolean} [shouldUpdateHash]
   */
  #activateTab(tabId, shouldFocus = false, shouldUpdateHash = false) {
    if (!tabId) return;
    const targetButton = this.#buttons.find((button) => button.dataset.tabId === tabId);
    if (!(targetButton instanceof HTMLButtonElement)) return;

    if (this.#activeId === tabId) {
      this.#updateActiveOffset(tabId);
      if (shouldUpdateHash) {
        this.#replaceHash(tabId);
      }
      return;
    }

    this.#activeId = tabId;

    for (const button of this.#buttons) {
      const isActive = button.dataset.tabId === tabId;
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.tabIndex = isActive ? 0 : -1;
      button.classList.toggle('is-active', isActive);

      if (isActive && shouldFocus) {
        button.focus();
      }
    }

    for (const panel of this.#panels) {
      const isActive = panel.dataset.tabPanel === tabId;
      panel.hidden = !isActive;
    }

    if (shouldUpdateHash) {
      this.#replaceHash(tabId);
    }

    this.#updateActiveOffset(tabId);
  }

  /**
   * @param {boolean} [shouldScrollToSection]
   */
  #activateFromHash(shouldScrollToSection = false) {
    const tabId = this.#tabIdFromHash();
    if (!tabId) return;

    if (this.#isDesktop()) {
      this.#activateTab(tabId, false, false);
      if (shouldScrollToSection) {
        this.#scrollToSection();
      }
      return;
    }

    this.#openMobileTab(tabId, shouldScrollToSection);
  }

  #isDesktop() {
    return this.#mobileBreakpoint.matches;
  }

  #tabIdFromHash() {
    const hash = decodeURIComponent(window.location.hash.replace(/^#/, '').trim());
    if (!hash) return '';
    if (!hash.startsWith('pdp-tab-')) return '';
    return hash.replace(/^pdp-tab-/, '');
  }

  /** @param {string} tabId */
  #replaceHash(tabId) {
    const nextHash = `#pdp-tab-${tabId}`;
    if (window.location.hash === nextHash) return;
    history.replaceState(null, '', nextHash);
  }

  #scrollToSection() {
    this.scrollIntoView({ behavior: 'auto', block: 'start' });
  }

  /**
   * @param {string} tabId
   * @param {boolean} [shouldScroll]
   */
  #openMobileTab(tabId, shouldScroll = false) {
    const target = this.#mobileItems.find((item) => item.dataset.tabId === tabId);
    if (!(target instanceof HTMLDetailsElement)) return;
    target.open = true;
    if (shouldScroll) {
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }

  /** @param {string | undefined} tabId */
  #updateActiveOffset(tabId) {
    if (!(this.#tabList instanceof HTMLElement) || !(this.#inner instanceof HTMLElement)) return;
    if (!window.matchMedia('(min-width: 1600px)').matches) {
      this.style.setProperty('--product-info-tabs-active-offset', '0px');
      return;
    }

    const activeButton = this.#buttons.find((button) => button.dataset.tabId === tabId);
    if (!(activeButton instanceof HTMLButtonElement)) return;

    const tabListRect = this.#tabList.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();
    const rawOffset = buttonRect.left - tabListRect.left;
    const maxOffset = this.#inner.clientWidth * 0.55;
    const clampedOffset = Math.max(0, Math.min(rawOffset, maxOffset));

    this.style.setProperty('--product-info-tabs-active-offset', `${Math.round(clampedOffset)}px`);
  }
}

if (!customElements.get('product-info-tabs')) {
  customElements.define('product-info-tabs', ProductInfoTabs);
}
